/**
 * ScriptSubsystem — the unified runtime for the `core.Script` component in the player.
 *
 * Wraps the backend-agnostic {@link ScriptHost}: it registers the TypeScript and Redprint
 * backends, loads their assets from the manifest, builds a per-entity
 * {@link GameObjectEngineContext}, and drives the unified lifecycle. It is the W4
 * `ScriptSubsystem` — both a TS Behaviour and a Redprint graph run through ONE
 * `IScriptExecutor` + ONE `IEngineContext`.
 *
 * This is now the SOLE script runtime in the player: the legacy player `RedprintSubsystem` /
 * `JSScriptSubsystem` were removed, and legacy `core.Redprint` components are migrated to
 * `core.Script{redprint}` on load (LevelSerializer) and in the build (ExportLevelsStep), so only
 * `core.Script` ever reaches this runtime.
 */

import {
  Subsystem,
  type SubsystemConfig,
  SubsystemPriority,
  Engine,
  Behaviour,
  Components,
  BackendRegistry,
  ScriptHost,
  buildEngineApi,
  cloneGameObject,
  makePhysicsBacking,
  makeVFXBacking,
  makeCameraBacking,
  type GameObject,
  type GUID,
  type IEngineContext,
  type InputCaps,
  type IScriptExecutor,
  type Level,
  type ScriptType,
  type TimeCaps,
} from "@engine/core";
import type { PhysicsSubsystem } from "./PhysicsSubsystem";
import type { AudioSubsystem } from "./AudioSubsystem";
import type { VFXSubsystem } from "./VFXSubsystem";
import type { RenderSubsystem } from "./RenderSubsystem";
import type { UISubsystem } from "./UISubsystem";
import {
  Graph,
  getDefaultRegistry,
  deserializeGraph,
  type NodeRegistry,
  type SerializedGraph,
} from "@engine/redprint";
import type { AssetSubsystem } from "./AssetSubsystem";
import type { InputSubsystem } from "./InputSubsystem";
import { GameObjectEngineContext, type GameObjectContextDeps } from "@engine/core";
import * as EngineCore from "@engine/core";
import { TypeScriptBackend, RedprintBackend } from "@engine/scripting";
import { GpuScriptBacking } from "../scripting/GpuScriptBacking";

/** Data shape of a `core.Script` component. */
interface ScriptComponentData {
  scriptType?: string;
  assetId: string;
  enabled?: boolean;
  variables?: Record<string, unknown>;
  priority?: number;
}

const VALID_TYPES = new Set<ScriptType>(["typescript", "redprint", "csharp"]);

export class ScriptSubsystem extends Subsystem {
  readonly config: SubsystemConfig = {
    name: "script",
    // After legacy redprint(User)/jsscript(User+1) so additive ordering is stable.
    priority: SubsystemPriority.User + 2,
    optional: true,
    dependencies: ["assets", "input"],
  };

  private readonly _registry = new BackendRegistry();
  private readonly _graphs = new Map<string, Graph>();
  private readonly _sources = new Map<string, string>();
  private _nodeRegistry: NodeRegistry = getDefaultRegistry();
  private _tsBackend: TypeScriptBackend | null = null;

  private _host: ScriptHost | null = null;
  private _deps: GameObjectContextDeps | null = null;
  private _level: Level | null = null;
  private _gpu: GpuScriptBacking | null = null;
  private _eventUnsubs: (() => void)[] = [];
  private _enabled = true;

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  async onInitialize(): Promise<void> {
    const assets = this.getSubsystem<AssetSubsystem>("assets");
    const manifest = assets.getManifest();

    // Load Redprint graphs.
    if (manifest?.assets.graphs) {
      for (const [id, path] of Object.entries(manifest.assets.graphs)) {
        try {
          const data = await assets.loadJson<SerializedGraph>(id, path);
          const graph = new Graph(deserializeGraph(data));
          this._graphs.set(id, graph);
          this._graphs.set(path, graph);
        } catch (e) {
          console.warn(`[ScriptSubsystem] Failed to load graph ${path}:`, e);
        }
      }
    }

    // Load compiled TypeScript Behaviour sources (CommonJS JS, produced by the editor
    // esbuild-transform step). Separate manifest category from the legacy `scripts`
    // (jsscript) so the two never collide.
    if (manifest?.assets.behaviours) {
      for (const [id, path] of Object.entries(manifest.assets.behaviours)) {
        try {
          const source = await assets.loadText(id, path);
          this._sources.set(id, source);
          this._sources.set(path, source);
        } catch (e) {
          console.warn(`[ScriptSubsystem] Failed to load behaviour ${path}:`, e);
        }
      }
    }

    // Register backends.
    this._tsBackend = new TypeScriptBackend(
      (id) => this._sources.get(id) ?? null,
      { "@engine/core": EngineCore },
    );
    this._registry.register(this._tsBackend);
    this._registry.register(new RedprintBackend((id) => this._graphs.get(id) ?? null, this._nodeRegistry));

    // Build the shared engine-context deps + the host.
    this._deps = this.buildContextDeps();
    this._host = new ScriptHost(this._registry, (entity) => this.contextFor(entity));

    console.log(
      `[ScriptSubsystem] Ready (${this._graphs.size / 2 || 0} graphs, ${this._sources.size / 2 || 0} behaviours)`,
    );
  }

  onUpdate(dt: number): void {
    if (this._enabled) this._host?.update(dt);
  }
  onFixedUpdate(dt: number): void {
    if (this._enabled) this._host?.fixedUpdate(dt);
  }
  /** Called from the player bridge ISystem's lateUpdate. */
  lateUpdate(dt: number): void {
    if (this._enabled) this._host?.lateUpdate(dt);
  }
  onPause(): void {
    this._enabled = false;
  }
  onResume(): void {
    this._enabled = true;
  }
  onShutdown(): void {
    this.destroyLevel();
    this._gpu?.dispose();
    this._gpu = null;
  }

  // ==========================================================================
  // LEVEL WIRING (called by LevelSubsystem)
  // ==========================================================================

  /** Instantiate scripts for every `core.Script` in the level + subscribe to scene changes. */
  initializeLevel(level: Level): void {
    this._level = level;
    for (const go of level.getAllObjects()) this.initializeObject(go);

    this._eventUnsubs.push(
      level.events.onObjectAdded(({ gameObject }) => this.initializeObject(gameObject)),
      level.events.onObjectRemoved(({ gameObject }) => this._host?.remove(gameObject.guid)),
    );

    console.log(`[ScriptSubsystem] Initialized ${this._host?.size ?? 0} script(s)`);
  }

  destroyLevel(): void {
    for (const unsub of this._eventUnsubs) unsub();
    this._eventUnsubs = [];
    this._host?.destroyAll();
    this._level = null;
  }

  /** For physics / external event delivery (symmetric across backends). */
  getExecutor(guid: GUID): IScriptExecutor | undefined {
    return this._host?.getExecutor(guid);
  }

  triggerEvent(guid: GUID, eventId: string, params?: Record<string, unknown>): void {
    this._host?.triggerEvent(guid, eventId, params);
  }

  // ==========================================================================
  // PRIVATE
  // ==========================================================================

  private initializeObject(go: GameObject): void {
    const data = go.getComponentData("core.Script") as unknown as ScriptComponentData | null;
    if (!data?.assetId) return;

    const scriptType = (data.scriptType ?? "typescript") as ScriptType;
    if (!VALID_TYPES.has(scriptType)) {
      console.warn(`[ScriptSubsystem] Unknown scriptType '${data.scriptType}' on ${go.name}`);
      return;
    }

    // A bad/missing asset (e.g. a not-yet-compiled behaviour) must not abort level load.
    try {
      this._host?.add({
        key: go.guid,
        entity: go.id,
        scriptType,
        assetId: data.assetId,
        enabled: data.enabled,
        variables: data.variables,
        priority: data.priority,
      });
    } catch (e) {
      console.error(`[ScriptSubsystem] Failed to start script on ${go.name} (${data.assetId}):`, e);
    }
  }

  private contextFor(entity: number): IEngineContext {
    const go = this._level?.findById(entity);
    if (!go) throw new Error(`[ScriptSubsystem] no GameObject for entity ${entity}`);
    return new GameObjectEngineContext(go, this._deps!);
  }

  private buildContextDeps(): GameObjectContextDeps {
    const input = this.getSubsystem<InputSubsystem>("input").manager;

    const time: TimeCaps = {
      get time() {
        return Engine.instance.time.time;
      },
      get deltaTime() {
        return Engine.instance.time.deltaTime;
      },
      get fixedDeltaTime() {
        return Engine.instance.time.fixedDeltaTime;
      },
    };

    const inputCaps: InputCaps = {
      getInput: (action) => {
        const v = input.getAction(action)?.readValue<number>();
        return typeof v === "number" ? v : 0;
      },
      isInputPressed: (action) => input.getAction(action)?.isPressed ?? false,
      isInputDown: (action) => input.getAction(action)?.wasPressed ?? false,
      isInputUp: (action) => input.getAction(action)?.wasReleased ?? false,
    };

    // Real physics backing for `engine.physics`: GUID-addressed PhysicsSubsystem resolved
    // lazily (it initializes after this subsystem), entity↔GUID via the live Level.
    const physics = makePhysicsBacking(
      () => {
        try {
          return this.getSubsystem<PhysicsSubsystem>("physics");
        } catch {
          return null;
        }
      },
      (entity) => this._level?.findById(entity)?.guid ?? null,
      (guid) => this._level?.findByGuid(guid)?.id ?? null,
    );

    // Real audio backing for `engine.audio`: lazy AudioSubsystem; play (preloaded) clips by GUID.
    const audioOf = () => {
      try {
        return this.getSubsystem<AudioSubsystem>("audio");
      } catch {
        return null;
      }
    };
    const guidOf = (e: number) => this._level?.findById(e)?.guid ?? null;
    const audio = {
      playOneShot: (clipGuid: string, volume?: number) =>
        audioOf()?.engine.playOneShot(clipGuid, volume != null ? { volume } : {}),
      play: (e: number) => { const g = guidOf(e); if (g) audioOf()?.playSource(g); },
      stop: (e: number) => { const g = guidOf(e); if (g) audioOf()?.stopSource(g); },
      pause: (e: number) => { const g = guidOf(e); if (g) audioOf()?.pauseSource(g); },
      resume: (e: number) => { const g = guidOf(e); if (g) audioOf()?.resumeSource(g); },
      stopAll: () => audioOf()?.engine.stopAll(),
    };

    // VFX backing: the player's VFXSubsystem (GUID-addressed), resolved lazily.
    const vfx = makeVFXBacking(
      () => { try { return this.getSubsystem<VFXSubsystem>("vfx"); } catch { return null; } },
      (entity) => this._level?.findById(entity)?.guid ?? null,
    );

    // Camera backing: Camera2D data (orthoSize) + active-camera switch via the RenderSubsystem.
    const renderOf = () => {
      try { return this.getSubsystem<RenderSubsystem>("render"); } catch { return null; }
    };
    const camera = makeCameraBacking(
      (entity) => this._level?.findById(entity) ?? null,
      () => {
        const id = renderOf()?.activeCameraEntity ?? null;
        return id != null ? this._level?.findById(id)?.guid ?? null : null;
      },
      (guid) => {
        const e = this._level?.findByGuid(guid)?.id;
        if (e != null) renderOf()?.setActiveCameraByEntity(e);
      },
    );

    // Scene spawn/destroy/instantiate backing — mirrors the editor's (Level-backed, immediate
    // destroy so removal events fire even without a pending-destroy drain in the loop).
    const scene = {
      spawn: (): number => this._level?.createGameObject()?.id ?? -1,
      destroy: (entity: number): void => {
        const go = this._level?.findById(entity);
        if (go) this._level?.destroyGameObjectImmediate(go);
      },
      instantiate: (source: number): number => {
        const level = this._level;
        const src = level?.findById(source);
        return level && src ? cloneGameObject(src, level).id : -1;
      },
    };

    // UI backing (engine.ui, UI-9): screen bridges live in the UISubsystem, keyed by GO guid —
    // handles survive canvas remounts; getScreen is valid even before the canvas mounts.
    const uiOf = () => {
      try { return this.getSubsystem<UISubsystem>("ui") ?? null; } catch { return null; }
    };
    const ui = {
      getScreen: (entity: number) => {
        const go = this._level?.findById(entity);
        if (!go || !go.getComponentData("core.UICanvas")) return null;
        const bridge = uiOf()?.screenFor(go.guid);
        if (!bridge) return null;
        return {
          setProps: (props: Record<string, unknown>) => bridge.setProps(props),
          on: (event: string, handler: (...args: unknown[]) => void) => bridge.on(event, handler),
        };
      },
      pointerOverUI: () => uiOf()?.pointerOverUI ?? false,
    };

    // GPU compute backing for `engine.gpu` v2 (W-GPU parity): raw device from the RenderSubsystem
    // context, kernels resolved against the build-emitted compute-shaders.json (no compiler).
    // Per-script cleanup happens through the GpuScope each context builds over this backing.
    this._gpu ??= new GpuScriptBacking(
      () => {
        try {
          return (renderOf()?.getContext()?.getDevice?.() ?? null) as GPUDevice | null;
        } catch {
          return null;
        }
      },
      () => {
        try {
          const assets = this.getSubsystem<AssetSubsystem>("assets");
          return assets
            ? { relPath: assets.getManifest()?.computeShaders, resolveUrl: (rel: string) => assets.resolveUrl(rel) }
            : null;
        } catch {
          return null;
        }
      },
    );

    return {
      resolve: (entity) => this._level?.findById(entity) ?? null,
      resolveGuid: (guid) => this._level?.findByGuid(guid)?.id ?? null,
      time,
      input: inputCaps,
      spawn: scene.spawn,
      destroy: scene.destroy,
      entities: () => (this._level ? [...this._level.getAllObjects()].map((go) => go.id) : []),
      engine: buildEngineApi({ physics, input: inputCaps, audio, vfx, camera, scene, ui, level: true }),
      gpu: this._gpu,
    };
  }
}
